/**
 * Builds the OpenStreetMap embed URL for the pickup-location map. Free, no API
 * key, no recurring cost — the calm default for "show me where I'm picking up."
 * Pure + dependency-free so the URL contract is unit-testable: a wrong bbox or a
 * dropped marker would point the customer at the wrong place.
 *
 * The embed takes a bounding box (the viewport) plus a marker (the pin). We
 * derive a small square box around the pin from `marginDeg` — smaller margin =
 * tighter zoom. 0.01° ≈ ~1km at mid-latitudes, a sensible "this block" default.
 */

/** Round to 6 decimals so float drift (21.32 − 0.01 = 21.310000000000002) can't leak into the URL. */
function r6(n: number): number {
  return Math.round(n * 1e6) / 1e6
}

export function osmEmbedSrc(
  latitude: number | null | undefined,
  longitude: number | null | undefined,
  marginDeg = 0.01,
): string | null {
  if (latitude == null || longitude == null) return null
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null
  const minLon = r6(longitude - marginDeg)
  const minLat = r6(latitude - marginDeg)
  const maxLon = r6(longitude + marginDeg)
  const maxLat = r6(latitude + marginDeg)
  const bbox = encodeURIComponent(`${minLon},${minLat},${maxLon},${maxLat}`)
  const marker = encodeURIComponent(`${r6(latitude)},${r6(longitude)}`)
  return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${marker}`
}
